/**
 * ImageGalleryPicker Component
 * 
 * Allows Tammy to browse and select from all available images
 * (menu items, gallery, special menus, events, uploads) when adding images.
 * Also includes an emoji picker for quick icons.
 */

import { useState, useEffect } from 'react';
import { HiX, HiSearch, HiPhotograph, HiShoppingBag, HiCollection, HiUpload, HiSparkles, HiEmojiHappy } from 'react-icons/hi';
import { adminFetch } from '../lib/adminApi';

interface ImageItem {
  id: string;
  url: string;
  name: string;
  source: 'menu' | 'gallery' | 'collection' | 'event' | 'uploads' | 'site';
  category: string;
  type: 'product' | 'gallery' | 'banner' | 'upload';
}

// Common bakery-related emojis for quick selection
const BAKERY_EMOJIS = [
  '🎂', '🍰', '🧁', '🍪', '🥧', '🥐', '🍩', '🥯', '🍞', '🥖',
  '🥨', '🧇', '🥞', '🍫', '🍬', '🍭', '🍮', '🎀', '💝', '❤️',
  '💖', '💕', '✨', '⭐', '🌟', '🎉', '🎊', '🎁', '🌸', '🌺',
  '🌹', '🌷', '🦋', '☕', '🥛', '🍓', '🫐', '🍒', '🍑', '🍎',
  '🍋', '🥜', '🍯', '🧈', '🥚', '🍦', '🧊', '🎈', '💐', '🎵'
];

interface ImageGalleryPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string) => void;
  currentImageUrl?: string;
}

export default function ImageGalleryPicker({
  isOpen,
  onClose,
  onSelect,
  currentImageUrl
}: ImageGalleryPickerProps) {
  const [images, setImages] = useState<ImageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSource, setSelectedSource] = useState<'all' | 'menu' | 'gallery' | 'collection' | 'event' | 'uploads' | 'site'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'images' | 'emojis'>('images');

  // API URL
  const API_URL = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    if (isOpen) {
      fetchImages();
    }
  }, [isOpen]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await adminFetch(`${API_URL}/api/images/all`);
      if (response.ok) {
        const data = await response.json();
        setImages(data.images || []);
      }
    } catch (err) {
      console.error('Failed to fetch images:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter images
  const filteredImages = images.filter(img => {
    // Search filter
    if (searchTerm && !img.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    // Source filter
    if (selectedSource !== 'all' && img.source !== selectedSource) {
      return false;
    }
    // Category filter
    if (selectedCategory !== 'all' && img.category !== selectedCategory) {
      return false;
    }
    return true;
  });

  // Get unique categories
  const categories = Array.from(new Set(images.map(img => img.category))).sort();

  // Get source counts
  const sourceCounts = {
    all: images.length,
    menu: images.filter(img => img.source === 'menu').length,
    gallery: images.filter(img => img.source === 'gallery').length,
    collection: images.filter(img => img.source === 'collection').length,
    event: images.filter(img => img.source === 'event').length,
    uploads: images.filter(img => img.source === 'uploads').length,
    site: images.filter(img => img.source === 'site').length
  };

  // Source display config
  const sourceConfig: Record<string, { label: string; icon: typeof HiPhotograph }> = {
    all: { label: 'All', icon: HiPhotograph },
    menu: { label: 'Menu', icon: HiShoppingBag },
    gallery: { label: 'Gallery', icon: HiPhotograph },
    collection: { label: 'Special Menus', icon: HiCollection },
    event: { label: 'Events', icon: HiSparkles },
    uploads: { label: 'Uploads', icon: HiUpload },
    site: { label: 'Event Banners', icon: HiSparkles }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {activeTab === 'images' ? 'Browse Images' : 'Choose an Emoji'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {activeTab === 'images' 
                ? `Select from ${images.length} available images`
                : 'Quick icons for categories and labels'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
            title="Close"
          >
            <HiX className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Tabs: Images vs Emojis */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('images')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
              activeTab === 'images'
                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <HiPhotograph className="w-5 h-5" />
            Images ({images.length})
          </button>
          <button
            onClick={() => setActiveTab('emojis')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
              activeTab === 'emojis'
                ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <HiEmojiHappy className="w-5 h-5" />
            Emojis ({BAKERY_EMOJIS.length})
          </button>
        </div>

        {/* Filters - Only show for images tab */}
        {activeTab === 'images' && (
          <div className="p-4 border-b border-gray-200 bg-gray-50 space-y-4">
            {/* Search */}
            <div className="relative">
              <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search images by name..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              />
            </div>

            {/* Source and Category Filters */}
            <div className="flex flex-wrap gap-3">
              {/* Source Filter */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-700">Source:</span>
                <div className="flex gap-1 flex-wrap">
                  {(['all', 'menu', 'gallery', 'collection', 'event', 'uploads', 'site'] as const).map(source => {
                    const config = sourceConfig[source];
                    const Icon = config.icon;
                    const count = sourceCounts[source];
                    // Hide sources with 0 items (except 'all')
                    if (source !== 'all' && count === 0) return null;
                    return (
                      <button
                        key={source}
                        onClick={() => setSelectedSource(source)}
                        className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          selectedSource === source
                            ? 'bg-purple-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {config.label} ({count})
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Category Filter */}
              {categories.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Category:</span>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none bg-white"
                    aria-label="Filter by category"
                    title="Filter by category"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'images' ? (
            // Images Tab Content
            loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full" />
                <span className="ml-3 text-gray-600">Loading images...</span>
              </div>
            ) : filteredImages.length === 0 ? (
              <div className="text-center py-12">
                <HiPhotograph className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">
                  {searchTerm || selectedSource !== 'all' || selectedCategory !== 'all'
                    ? 'No images match your filters'
                    : 'No images available'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filteredImages.map((image) => {
                  const SourceIcon = sourceConfig[image.source]?.icon || HiPhotograph;
                  return (
                    <button
                      key={image.id}
                      onClick={() => {
                        onSelect(image.url);
                        onClose();
                      }}
                      className={`group relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        currentImageUrl === image.url
                          ? 'border-purple-600 ring-2 ring-purple-200'
                          : 'border-gray-200 hover:border-purple-400 hover:shadow-lg'
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      {/* Overlay with info */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-2">
                          <p className="text-white text-xs font-medium truncate">{image.name}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <SourceIcon className="w-3 h-3 text-white" />
                            <span className="text-white text-xs">{image.category}</span>
                          </div>
                        </div>
                      </div>
                      {/* Selected indicator */}
                      {currentImageUrl === image.url && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )
          ) : (
            // Emojis Tab Content
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Click an emoji to use it as an icon or decoration. Great for category labels!
              </p>
              <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-3">
                {BAKERY_EMOJIS.map((emoji, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      // For emojis, we pass the emoji character itself
                      // The parent component can decide how to use it
                      onSelect(emoji);
                      onClose();
                    }}
                    className="text-4xl p-3 rounded-lg border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50 hover:scale-110 transition-all"
                    title={`Select ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            {activeTab === 'images' 
              ? `Showing ${filteredImages.length} of ${images.length} images`
              : `${BAKERY_EMOJIS.length} bakery emojis available`}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
